/**
 * The header bar's download tray (VE-26426).
 *
 * Lists every file that has begun — downloading, done or failed — with its state,
 * as the per-file detail behind the summary the progress toasts show. Files still
 * queued are a count on one line above rather than a row each: three stream at a
 * time, so a run of fifteen would otherwise bury the three that are moving under
 * twelve identical waiting rows.
 *
 * Reads the downloads duck, so it covers batches started anywhere in the app: the
 * folder view, the media editor or the document editor, and more than one at a
 * time.
 *
 * Flat rather than grouped by batch: which run a file belongs to is bookkeeping
 * the user did not ask for, and the file names already say what is being written.
 * Clear removes the finished batches, leaving anything still running alone.
 *
 * A finished row can offer to open the file, and only that. There is no web API
 * to reveal a file in the OS file manager or hand it to the default application —
 * Chrome's own download bubble does those because it is the browser, not a page.
 * All a page can do is read the file back and put it in a tab. That covers every
 * format Redact writes — media and PDFs directly, and the audit-log CSV once it
 * is relabelled as text — but not a format with no viewer at all, where the tab
 * would just download a second copy. See ./downloadedFiles.
 *
 * Mounted from AppBar beside CustomNotification, because the icon that opens it
 * lives in the aiWARE app-bar widget rather than in this app's React tree (see
 * AppBar/downloadTrayIcon.tsx). That icon being outside the tray is also why the
 * click-away handler has to exempt it: it already toggles, and closing on it too
 * would leave the two fighting over the same click.
 */
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { ThemeProvider } from '@mui/material/styles';

import type { IntlShape } from 'react-intl';

import { I18nTranslate } from '@i18n';
import theme from '@redact/materialUITheme';
import {
  CLEAR_FINISHED_DOWNLOADS,
  CLOSE_DOWNLOAD_TRAY,
  DownloadFileState,
  selectDownloadFiles,
  selectHasFinishedDownloads,
  selectIsDownloadTrayOpen,
} from '@redact-modules/downloads';
import { DOWNLOAD_ACTION_ID } from '@redact-components/AppBar/downloadTrayIcon';

import {
  forgetDownloadedFiles,
  hasDownloadedFile,
  isViewableInBrowser,
  openDownloadedFile,
} from './downloadedFiles';
import { formatDownloadProgress, formatDownloadSize } from './formatSize';
import { useStyles } from './styles';

const ICON_SIZE = 18;

const STATUS_MESSAGE = {
  done: { id: 'downloadTrayFileDone', defaultMessage: 'Done' },
  error: { id: 'downloadTrayFileError', defaultMessage: 'Failed' },
  queued: { id: 'downloadTrayFileQueued', defaultMessage: 'Waiting to start' },
  downloading: {
    id: 'downloadTrayFileDownloading',
    defaultMessage: 'Downloading',
  },
} as const;

/**
 * Decorative except for the spinner: every other state is already spelled out in
 * the row's detail line, so labelling the icon too would have a screen reader
 * announce each row's status twice. A downloading row's detail is a byte count,
 * which doesn't say what state it is in — so that one icon is labelled.
 */
const StatusIcon = ({
  status,
  downloadingLabel,
}: {
  status: DownloadFileState['status'];
  downloadingLabel: string;
}) => {
  switch (status) {
    case 'done':
      return <CheckCircleIcon color="success" sx={{ fontSize: ICON_SIZE }} />;
    case 'error':
      return <ErrorOutlineIcon color="error" sx={{ fontSize: ICON_SIZE }} />;
    case 'downloading':
      return (
        <CircularProgress size={ICON_SIZE} aria-label={downloadingLabel} />
      );
    default:
      return <ScheduleIcon color="disabled" sx={{ fontSize: ICON_SIZE }} />;
  }
};

/**
 * What a row says under the file name.
 *
 * A file still going shows how far it has got. Anything else pairs its size with
 * its state — "5.4 MB • Done" — which is the shape a download list is expected
 * to have, and the size is the thing worth knowing once the transfer is over.
 * Where no size was ever established the state stands alone rather than being
 * padded with a guess.
 */
const rowDetail = (file: DownloadFileState, intl: IntlShape): string => {
  if (file.status === 'downloading') {
    return typeof file.total === 'number'
      ? formatDownloadProgress(file.loaded, file.total)
      : intl.formatMessage(
          {
            id: 'downloadTrayFileProgressNoSize',
            defaultMessage: '{loaded} downloaded',
          },
          { loaded: formatDownloadSize(file.loaded) }
        );
  }

  const status = intl.formatMessage(STATUS_MESSAGE[file.status]);
  const size = file.total ?? (file.status === 'done' ? file.loaded : undefined);
  return typeof size === 'number'
    ? intl.formatMessage(
        { id: 'downloadTraySizeStatus', defaultMessage: '{size} • {status}' },
        { size: formatDownloadSize(size), status }
      )
    : status;
};

const DownloadTrayContent = () => {
  const intl = I18nTranslate.Intl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const files = useSelector(selectDownloadFiles);
  const isOpen = useSelector(selectIsDownloadTrayOpen);
  const hasFinished = useSelector(selectHasFinishedDownloads);

  const close = useCallback(() => {
    dispatch(CLOSE_DOWNLOAD_TRAY());
  }, [dispatch]);

  /**
   * A finished batch stays in the tray so it can be looked at, which means the
   * user needs a way to be rid of it — otherwise the header icon sits there
   * until the next download or a page reload. Clears only what has finished, so
   * it can never read as a cancel for something still running.
   */
  const clear = useCallback(() => {
    // Exactly the batches the reducer is about to drop, which is not the same
    // as "every batch with a file that isn't downloading": a batch part-way
    // through has done rows too, and naming it here threw away the handles of
    // rows that stayed on screen — those rows lost their Open button while
    // files the same batch finished later kept theirs.
    const stillRunning = new Set(
      files
        .filter(
          (file) => file.status === 'queued' || file.status === 'downloading'
        )
        .map((file) => file.batchId)
    );
    forgetDownloadedFiles(
      [...new Set(files.map((file) => file.batchId))].filter(
        (batchId) => !stillRunning.has(batchId)
      )
    );
    dispatch(CLEAR_FINISHED_DOWNLOADS());
  }, [dispatch, files]);

  /**
   * Offered only where it would actually show the file. There is no web API to
   * hand a file to the OS — no "reveal in folder", no "open with the default
   * app"; Chrome's download bubble can do those because it is the browser. A
   * page can only read the file back and put it in a tab, which covers every
   * format Redact writes but not one with no viewer at all. See
   * ./downloadedFiles.
   */
  const canOpen = (file: (typeof files)[number]) =>
    file.status === 'done' &&
    isViewableInBrowser(file.name) &&
    hasDownloadedFile(file.batchId, file.index);

  /**
   * Clicking off the tray closes it, as its sibling popover does — but never a
   * click on the header icon that opens it. That click already toggles, and
   * whether this listener or the widget's own handler runs first is the SDK's
   * business, not something to depend on: leaving it to event order would mean
   * the icon either reopened the tray it just closed or appeared to do nothing.
   *
   * Tested in both directions. `closest` alone only catches a click on the icon
   * or inside it, and the widget wraps that markup in an action slot of its own —
   * so a click on the slot's padding lands on an ANCESTOR, where `closest` finds
   * nothing and the race this exemption exists to avoid happens anyway.
   */
  const onClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      const action = document.getElementById(DOWNLOAD_ACTION_ID);
      if (
        target &&
        action &&
        (action.contains(target) || target.contains(action))
      ) {
        return;
      }
      close();
    },
    [close]
  );

  // Escape closes the tray, matching every other dismissible surface in the app.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  const title = intl.formatMessage({
    id: 'downloadTrayTitle',
    defaultMessage: 'Downloads',
  });
  const downloadingLabel = intl.formatMessage({
    id: 'downloadTrayFileDownloading',
    defaultMessage: 'Downloading',
  });
  const openLabel = intl.formatMessage({
    id: 'downloadTrayOpen',
    defaultMessage: 'Open',
  });

  /**
   * Everything not yet started, across every batch. Three files stream at a
   * time, so most of a large batch sits here — as a count rather than as rows,
   * which keeps a batch of fifteen from burying the three that are moving.
   */
  const queuedCount = files.filter((file) => file.status === 'queued').length;

  /**
   * The rows: everything that has actually begun. The queued ones are the line
   * above, not twelve repetitions of "Waiting to start" pushing the three that
   * are moving off the bottom of the panel.
   */
  const startedFiles = files.filter((file) => file.status !== 'queued');

  // No session means nothing to describe. Guarding on the files rather than on
  // `isOpen` alone also makes a stray click on the hidden header icon a no-op.
  if (!isOpen || files.length === 0) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Paper
        className={classes.trayContainer}
        // Not `dialog`: nothing here traps focus or blocks the page, and claiming
        // to be a modal that isn't one is worse for a screen reader than being
        // named honestly.
        role="region"
        aria-label={title}
        data-testid="download-tray"
      >
        <Box className={classes.trayHeader}>
          <span className={classes.trayTitle}>{title}</span>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasFinished && (
              <Button onClick={clear} size="small" data-testid="clear">
                {I18nTranslate.TranslateMessage('downloadTrayClear')}
              </Button>
            )}
            <IconButton
              onClick={close}
              size="small"
              data-testid="close"
              aria-label={intl.formatMessage({
                id: 'downloadTrayClose',
                defaultMessage: 'Close downloads',
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        {queuedCount > 0 && (
          <div
            className={classes.trayQueued}
            data-testid="download-tray-queued"
          >
            {intl.formatMessage(
              {
                id: 'downloadTrayQueuedCount',
                defaultMessage: 'Files queued: {count}',
              },
              { count: queuedCount }
            )}
          </div>
        )}
        <ul className={classes.trayList}>
          {startedFiles.map((file) => (
            <li
              key={`${file.batchId}-${file.index}`}
              className={classes.trayRow}
              data-testid="download-tray-row"
            >
              <StatusIcon
                status={file.status}
                downloadingLabel={downloadingLabel}
              />
              <div className={classes.rowText}>
                <span className={classes.rowName} title={file.name}>
                  {file.name}
                </span>
                <span className={classes.rowDetail}>
                  {rowDetail(file, intl)}
                </span>
              </div>
              {canOpen(file) && (
                <IconButton
                  size="small"
                  data-testid="open-downloaded-file"
                  title={openLabel}
                  aria-label={openLabel}
                  onClick={() => {
                    void openDownloadedFile(file.batchId, file.index);
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: ICON_SIZE }} />
                </IconButton>
              )}
            </li>
          ))}
        </ul>
      </Paper>
    </ClickAwayListener>
  );
};

const DownloadTray = () => (
  <ThemeProvider theme={theme}>
    <DownloadTrayContent />
  </ThemeProvider>
);

export default DownloadTray;
